import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegisterWithForm } from '../hooks/useRegisterWithForm';
import { useEmailTypoDetection } from '../hooks/useEmailTypoDetection';
import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { PasswordInput } from '../../../components/ui/password-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import PageWrap from '../../../components/auth/page-wrap';
import H1 from '../../../components/layout/h1';
import { CLASSES } from '../../../constants/classes';

interface RegisterPageProps {
  forRole: string;
}

export default function RegisterPage({ forRole }: RegisterPageProps) {
  const { PATHS, PAGES } = useRoutesProvider();
  const navigate = useNavigate();
  const { form, onSubmit, redirectToLogIn, loading, isElev } = useRegisterWithForm(forRole);
  const emailTypo = useEmailTypoDetection(form as any, 'email');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Type assertions for form control based on role
  const elevForm = form as any;
  const profesorForm = form as any;

  // Check if any form field has been filled
  const hasFormData = () => {
    const formValues = form.getValues();
    return Object.values(formValues).some(value =>
      value !== undefined && value !== null && value !== ''
    );
  };

  // Handle back button click
  const handleBackClick = () => {
    if (hasFormData()) {
      setShowConfirmDialog(true);
    } else {
      navigate(PATHS[PAGES.HOME]);
    }
  };

  // Confirm navigation away from form
  const confirmNavigation = () => {
    setShowConfirmDialog(false);
    navigate(PATHS[PAGES.HOME]);
  };

  return (
    <PageWrap>
      {/* Top Button: Already have account */}
      <div className="form-top-button-container">
        <div className="form-button-wrapper">
          <Button
            type="button"
            size="lg"
            disabled={loading}
            className="m-2 bg-magenta text-white touch-target"
            onClick={redirectToLogIn}
          >
            Am deja un cont creat!
          </Button>
        </div>
      </div>

      {/* Main Form Container */}
      <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        {/* Un singur scroll, cel al paginii (PageWrap, P0-6) — pe toate latimile. Zona
            avea propria fereastra de 60dvh peste 768px (`md:max-h-[60dvh]
            md:overflow-y-scroll`): butonul de trimitere e ULTIMUL element din acelasi
            div, deci ramanea in cutia derulabila si putea iesi din fereastra vizibila
            fara niciun semnal (P0-5, masurat: 203px sub margine la incarcare pe
            /elev/inregistrare). Doua zone derulabile suprapuse (asta + PageWrap) ar fi
            exact regresia de la PR #30 — de-aia zona nu mai are propriul scroll, nu
            i se adauga unul PageWrap-ului. */}
        <div className="z-10 my-auto flex w-full flex-1 flex-col">
          <H1 customStyles="text-center text-fluid-2xl" text="Acum vreau să creez contul" />

          <Form {...elevForm}>
             <form onSubmit={onSubmit} className="flex w-full flex-col space-y-4">
              {isElev && (
                <>
                  {/* Row 1: Clasa - Horizontal Layout */}
                  <FormField
                    control={elevForm.control}
                    name="clasa"
                    /* `FormItem` invelie tot randul (eticheta + camp), nu doar campul -- altfel
                       `FormLabel` citeste un `FormItemContext` gol (e sora, nu fiica), si
                       `htmlFor` nu mai corespunde cu id-ul real al campului (constatarea
                       grupului 3b: 13 din 15 campuri fara eticheta asociata programatic). */
                    render={({ field }) => (
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">
                          Selectați clasa:
                        </FormLabel>
                        <div className="flex-1">
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="form-select-styled" aria-required="true">
                                <SelectValue placeholder="Selectați clasa în care sunteți" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="form-select-content">
                              {CLASSES.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                  className="form-select-item"
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Row 2: Cod Profesor - Horizontal Layout */}
                  <FormField
                    control={elevForm.control}
                    name="codProfesor"
                    render={({ field }) => (
                      <div className="space-y-2">
                        <FormItem className="form-field-row">
                          <FormLabel className="form-label-bold">
                            Codul profesorului:
                          </FormLabel>
                          <div className="flex-1 space-y-0">
                            <FormControl>
                              <Input
                                {...field}
                                type="text"
                                placeholder="Codul profesorului coordonator"
                                className="form-input-styled"
                                aria-required="true"
                              />
                            </FormControl>
                            <FormMessage className="form-message-spacer" />
                          </div>
                        </FormItem>
                        <FormDescription className="form-description-text">
                          *îl puteți obține de la profesorul coordonator.
                        </FormDescription>
                      </div>
                    )}
                  />

                  {/* Row 3: Nickname - Horizontal Layout */}
                  <FormField
                    control={elevForm.control}
                    name="nume"
                    render={({ field }) => (
                      <div className="space-y-2">
                        <FormItem className="form-field-row">
                          <FormLabel className="form-label-bold">
                            Nickname:
                          </FormLabel>
                          <div className="flex-1 space-y-0">
                            <FormControl>
                              <Input
                                {...field}
                                type="text"
                                autoComplete="username"
                                className="form-input-styled"
                                aria-required="true"
                              />
                            </FormControl>
                            <FormMessage className="form-message-spacer" />
                          </div>
                        </FormItem>
                        <FormDescription className="form-description-text">
                          *vă rugăm să alegeți un nume cât mai fantezist, nu numele vostru.
                          <br />
                          *acest nickname va reflecta acumularea punctelor
                        </FormDescription>
                      </div>
                    )}
                  />

                  {/* Row 4: Email - Horizontal Layout */}
                  <FormField
                    control={elevForm.control}
                    name="email"
                    render={({ field }) => (
                      <div className="space-y-2">
                        <FormItem className="form-field-row">
                          <FormLabel className="form-label-bold">
                            Email:
                          </FormLabel>
                          <div className="flex-1 space-y-0">
                            <FormControl>
                              <Input
                                {...field}
                                type="email"
                                autoComplete="email"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                className="form-input-styled"
                                aria-required="true"
                                onBlur={(e) => {
                                  field.onBlur();
                                  emailTypo.checkEmail(e.target.value);
                                }}
                              />
                            </FormControl>
                            <FormMessage className="form-message-spacer" />
                            {emailTypo.suggestion && (
                              <p className="text-sm text-warning mt-1">
                                Ai vrut să scrii{' '}
                                <button type="button" className="my-1.5 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 align-middle font-semibold underline text-magenta hover:text-magenta-hover md:my-0 md:min-h-0 md:min-w-0 md:px-0" onClick={emailTypo.applySuggestion}>
                                  {emailTypo.suggestion}
                                </button>
                                ?{' '}
                                <button type="button" className="my-1.5 ml-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 align-middle text-xs text-muted-foreground hover:underline md:ml-0 md:my-0 md:min-h-0 md:min-w-0 md:px-0" onClick={emailTypo.dismissSuggestion}>
                                  Nu
                                </button>
                              </p>
                            )}
                            {emailTypo.isEmailRoDomain && !emailTypo.suggestion && (
                              <p className="text-sm text-warning mt-1">
                                ⚠ Adresa @email.ro nu este un furnizor real de email. Verifică adresa.
                              </p>
                            )}
                          </div>
                        </FormItem>
                        <FormDescription className="form-description-text">
                          *vă rugăm să folosiți o adresă de email creată pentru această campanie, cu
                          nickname-ul folosit și nu cu numele vostru real.
                        </FormDescription>
                      </div>
                    )}
                  />

                  {/* P1-6: parolele foloseau `form-two-column` (main.css), doua campuri pe
                      un rand cu eticheta deasupra — singura exceptie din formular fata de
                      `form-field-row` (eticheta 200px, camp la dreapta), folosit de restul
                      celor 7 randuri. Linia pe care incep inputurile sarea de la 216px la
                      8px si revenea, coborand prin formular (masurat). Decizie owner:
                      parolele intra in grila, un camp pe rand — costul de +50px e acceptabil
                      acum ca PageWrap e derulabil (P0-6/p6). `form-two-column` ramane in
                      main.css, neatins — nu mai are niciun consumator dupa schimbarea asta,
                      dar main.css e denylist si stergerea utilitatii nu era ceruta. */}
                  <FormField
                    control={elevForm.control}
                    name="parola"
                    render={({ field }) => (
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">
                          Parola:
                        </FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <PasswordInput
                              {...field}
                              autoComplete="new-password"
                              className="form-input-styled"
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={elevForm.control}
                    name="confirmaParola"
                    render={({ field }) => (
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">
                          Confirmă parola:
                        </FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <PasswordInput
                              {...field}
                              autoComplete="new-password"
                              className="form-input-styled"
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    )}
                  />
                </>
              )}

              {!isElev && (
                <>
                  {/* Row 1: Nume si Prenume - Horizontal Layout */}
                  <FormField
                    control={profesorForm.control}
                    name="nume"
                    render={({ field }) => (
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">
                          Nume și Prenume:
                        </FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              type="text"
                              autoComplete="name"
                              className="form-input-styled"
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Row 2: Telefon - Horizontal Layout */}
                  <FormField
                    control={profesorForm.control}
                    name="telefon"
                    render={({ field }) => (
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">
                          Telefon:
                        </FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              type="tel"
                              autoComplete="tel"
                              placeholder="07xxxxxxxx"
                              className="form-input-styled"
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Row 3: Email - Horizontal Layout */}
                  <FormField
                    control={profesorForm.control}
                    name="email"
                    render={({ field }) => (
                      <div className="space-y-2">
                        <FormItem className="form-field-row">
                          <FormLabel className="form-label-bold">
                            Email:
                          </FormLabel>
                          <div className="flex-1 space-y-0">
                            <FormControl>
                              <Input
                                {...field}
                                type="email"
                                autoComplete="email"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                className="form-input-styled"
                                aria-required="true"
                                onBlur={(e) => {
                                  field.onBlur();
                                  emailTypo.checkEmail(e.target.value);
                                }}
                              />
                            </FormControl>
                            <FormMessage className="form-message-spacer" />
                            {emailTypo.suggestion && (
                              <p className="text-sm text-warning mt-1">
                                Ai vrut să scrii{' '}
                                <button type="button" className="my-1.5 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 align-middle font-semibold underline text-magenta hover:text-magenta-hover md:my-0 md:min-h-0 md:min-w-0 md:px-0" onClick={emailTypo.applySuggestion}>
                                  {emailTypo.suggestion}
                                </button>
                                ?{' '}
                                <button type="button" className="my-1.5 ml-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 align-middle text-xs text-muted-foreground hover:underline md:ml-0 md:my-0 md:min-h-0 md:min-w-0 md:px-0" onClick={emailTypo.dismissSuggestion}>
                                  Nu
                                </button>
                              </p>
                            )}
                            {emailTypo.isEmailRoDomain && !emailTypo.suggestion && (
                              <p className="text-sm text-warning mt-1">
                                ⚠ Adresa @email.ro nu este un furnizor real de email. Verifică adresa.
                              </p>
                            )}
                          </div>
                        </FormItem>
                        <FormDescription className="form-description-text">
                          *vă rugăm să verificați constant această adresă, deoarece vom trimite constant
                          informări.
                        </FormDescription>
                      </div>
                    )}
                  />

                  {/* P1-6: acelasi fix ca la formularul de elev, mai sus. */}
                  <FormField
                    control={profesorForm.control}
                    name="parola"
                    render={({ field }) => (
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">
                          Parola:
                        </FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <PasswordInput
                              {...field}
                              autoComplete="new-password"
                              className="form-input-styled"
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={profesorForm.control}
                    name="confirmaParola"
                    render={({ field }) => (
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">
                          Confirmă parola:
                        </FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <PasswordInput
                              {...field}
                              autoComplete="new-password"
                              className="form-input-styled"
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    )}
                  />
                </>
              )}

              <Button size="lg" disabled={loading} className="m-2 touch-target" type="submit">
                Înregistrare!
              </Button>
            </form>
          </Form>
        </div>
      </div>

      {/* Bottom Button: Back to home */}
      <div className="form-bottom-button-container">
        <div className="form-button-wrapper">
          <Button
            type="button"
            size="lg"
            disabled={loading}
            className="m-2 touch-target"
            onClick={handleBackClick}
          >
            Înapoi la pagina principală
          </Button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sigur doriți să părăsiți pagina?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Aveți date completate care vor fi pierdute dacă părăsiți această pagină.
            </p>
          </div>
          <DialogFooter className="flex gap-2 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Nu, rămân pe pagină
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmNavigation}
            >
              Da, vreau să ies
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageWrap>
  );
}
